import ExcelJS from "exceljs";
// src/utils/discountDayOfWeekCsvUtils.js

/**
 * Convert an array of objects into a CSV string.
 * Headers are object keys from the first element.
 * @param {Object[]} objArray
 * @returns {string}
 */
export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let str = val == null ? "" : String(val);
    if (str.includes('"')) str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  // header row
  let csv = headers.map(escape).join(",") + "\r\n";

  // data rows
  objArray.forEach((row) => {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  });

  return csv;
}

// export function exportDiscountDayOfWeekCSV(
//   transactions,
//   brandName = "Angels Pizza",
//   fileName = "Transactions_Discount Value_Day of Week.csv"
// ) {
//   if (!Array.isArray(transactions)) {
//     console.error("exportDiscountDayOfWeekCSV: expected an array");
//     return;
//   }

//   // exclude zero-discount transactions
//   const withDiscount = transactions.filter(
//     (tx) => Number(tx.discount_amount || 0) > 0
//   );

//   // map to flat objects matching headers
//   const rows = withDiscount.map((tx) => {
//     const date = new Date(tx.tran_date).toISOString().split("T")[0];
//     const store = tx.store_code.split("_")[1] || tx.store_code;
//     return {
//       Brand: brandName,
//       Store: store,
//       Date: date,
//       "Discount Name": tx.discount_name || "",
//       "Discount Amount": Number(tx.discount_amount || 0).toFixed(2),
//     };
//   });

//   // convert to CSV
//   const csvString = convertToCSV(rows);
//   if (!csvString) {
//     console.warn("exportDiscountDayOfWeekCSV: no data to export");
//     return;
//   }

//   // trigger download
//   const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }

export async function exportDiscountDayOfWeekCSV(
  transactions,
  brandName = "Angels Pizza",
  fileName = "Transactions_Discount_Value_Day_of_Week.xlsx",
) {
  if (!Array.isArray(transactions)) {
    console.error("exportDiscountDayOfWeekXLSX: expected an array");
    return;
  }

  // 1) keep only discounted transactions
  const withDiscount = transactions.filter(
    (tx) => Number(tx?.discount_amount || 0) > 0,
  );

  if (!withDiscount.length) {
    console.warn("exportDiscountDayOfWeekXLSX: no data to export");
    return;
  }

  // Helpers
  const getStore = (store_code) => {
    if (typeof store_code !== "string") return "";
    if (!store_code.includes("_")) return store_code;
    return store_code.split("_").slice(1).join("_");
  };

  const getDayName = (dateObj) =>
    dateObj.toLocaleString("en-US", { weekday: "long" });

  // 2) build rows (include Day of Week + Time)
  const rows = withDiscount.map((tx) => {
    const d = new Date(tx.tran_date);
    const dateISO = isNaN(d) ? "" : d.toISOString().split("T")[0];
    const timeHM = isNaN(d) ? "" : d.toTimeString().slice(0, 5);
    const day = isNaN(d) ? "" : getDayName(d);

    return {
      Brand: brandName,
      Store: getStore(tx.store_code),
      Date: dateISO,
      DayOfWeek: day,
      Time: timeHM,
      DiscountName: tx.discount_name || "",
      DiscountAmount: Number(tx.discount_amount || 0),
    };
  });

  // Sort by Day then Date then Time (nice for Day-of-Week reports)
  const dayOrder = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };
  rows.sort((a, b) => {
    const da = dayOrder[a.DayOfWeek] ?? 99;
    const db = dayOrder[b.DayOfWeek] ?? 99;
    if (da !== db) return da - db;
    if (a.Date !== b.Date) return (a.Date || "").localeCompare(b.Date || "");
    return (a.Time || "").localeCompare(b.Time || "");
  });

  // Totals
  const totalDiscount = rows.reduce(
    (sum, r) => sum + (r.DiscountAmount || 0),
    0,
  );

  // Optional: totals per day (for quick summary sheet)
  const totalsByDay = rows.reduce((acc, r) => {
    const k = r.DayOfWeek || "Unknown";
    acc[k] = (acc[k] || 0) + (r.DiscountAmount || 0);
    return acc;
  }, {});

  // ===== Workbook =====
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Analytics Export";
  workbook.created = new Date();

  // ===== Sheet 1: Raw Transactions =====
  const sheet = workbook.addWorksheet("Discount Transactions", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  // Title
  sheet.mergeCells("A1:G1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "Discount Transactions (Day of Week)";
  titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F2937" },
  };
  sheet.getRow(1).height = 26;

  // Meta
  sheet.mergeCells("A2:G2");
  const metaCell = sheet.getCell("A2");
  metaCell.value = `Brand: ${brandName}   |   Records: ${rows.length.toLocaleString()}   |   Total Discount: ₱${totalDiscount.toLocaleString(
    undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  )}`;
  metaCell.font = { italic: true, size: 11, color: { argb: "FF111827" } };
  metaCell.alignment = { vertical: "middle", horizontal: "left" };
  metaCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };
  sheet.getRow(2).height = 18;

  sheet.addRow([]);
  sheet.getRow(3).height = 6;

  // Header
  const headers = [
    "Brand",
    "Store",
    "Date",
    "Day of Week",
    "Time",
    "Discount Name",
    "Discount Amount",
  ];
  sheet.addRow(headers);

  // Data
  rows.forEach((r) => {
    sheet.addRow([
      r.Brand,
      r.Store,
      r.Date,
      r.DayOfWeek,
      r.Time,
      r.DiscountName,
      r.DiscountAmount,
    ]);
  });

  // Column widths
  sheet.columns = [
    { width: 16 }, // Brand
    { width: 18 }, // Store
    { width: 14 }, // Date
    { width: 14 }, // Day
    { width: 10 }, // Time
    { width: 26 }, // Discount Name
    { width: 18 }, // Amount
  ];

  const headerRowIndex = 4;
  const firstDataRow = headerRowIndex + 1;
  const lastDataRow = headerRowIndex + rows.length;

  // Style header
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 18;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2563EB" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Style body
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    const row = sheet.getRow(r);
    const isAlt = (r - firstDataRow) % 2 === 1;

    row.eachCell((cell, col) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      // alignment + formats
      if (col <= 6) {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      } else {
        cell.alignment = { vertical: "middle", horizontal: "right" };
        cell.numFmt = '"₱"#,##0.00';
      }

      if (isAlt) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
      }
    });
  }

  // TOTAL row
  const totalRowIndex = lastDataRow + 1;
  sheet.mergeCells(`A${totalRowIndex}:F${totalRowIndex}`);
  const totalLabelCell = sheet.getCell(`A${totalRowIndex}`);
  totalLabelCell.value = "TOTAL DISCOUNT";
  totalLabelCell.font = { bold: true };
  totalLabelCell.alignment = { vertical: "middle", horizontal: "left" };
  totalLabelCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  const totalCell = sheet.getCell(`G${totalRowIndex}`);
  totalCell.value = { formula: `SUM(G${firstDataRow}:G${lastDataRow})` };
  totalCell.font = { bold: true };
  totalCell.alignment = { vertical: "middle", horizontal: "right" };
  totalCell.numFmt = '"₱"#,##0.00';
  totalCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  // Auto-filter
  sheet.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: headers.length },
  };

  // ===== Sheet 2: Summary by Day (nice for Day-of-Week chart validation) =====
  const summary = workbook.addWorksheet("Summary by Day");
  summary.columns = [
    { header: "Day of Week", key: "day", width: 16 },
    { header: "Total Discount Amount", key: "total", width: 24 },
  ];

  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  dayNames.forEach((d) => {
    summary.addRow({ day: d, total: totalsByDay[d] || 0 });
  });

  // Style summary header
  summary.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF10B981" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Format currency in summary
  for (let r = 2; r <= 8; r++) {
    const cell = summary.getCell(`B${r}`);
    cell.numFmt = '"₱"#,##0.00';
    cell.alignment = { vertical: "middle", horizontal: "right" };
  }

  // Add total row
  summary.addRow({ day: "TOTAL", total: totalDiscount });
  const totalRow = summary.getRow(9);
  totalRow.eachCell((cell, col) => {
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEFF6FF" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    if (col === 2) {
      cell.numFmt = '"₱"#,##0.00';
      cell.alignment = { vertical: "middle", horizontal: "right" };
    }
  });

  // ===== Download (no file-saver) =====
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(link.href), 500);
}
